import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { CheckCircle2, XCircle, AlertTriangle, Circle, Activity, ChevronRight } from 'lucide-react';
import { instances as instancesApi, type BridgeBefund } from '../api';

/**
 * Ob eine Verknüpfung wirklich trägt — nicht ob ein Schlüssel hinterlegt ist.
 *
 * ══ WARUM DER UNTERSCHIED ZÄHLT ════════════════════════════════════════════
 *
 * Die Zeile daneben zeigt „Schlüssel otk_12541eb…". Das sagt, dass der PC
 * einen hat. Am 29.09.2026 stimmte das den ganzen Tag, während die Verbindung
 * nicht stand — an drei verschiedenen Stellen nacheinander: falsches
 * Schlüsselformat, im PC erneuert aber nicht ausgerollt, und nur eine der
 * beiden Seiten ausgerollt. Alle drei sahen hier gleich aus.
 *
 * Deshalb ist der letzte Schritt eine echte Anfrage an die Gast-Instanz. Was
 * die beantwortet, ist keine Vermutung mehr.
 */

const FARBE: Record<string, string> = {
  gruen: 'text-emerald-600',
  gelb: 'text-amber-600',
  rot: 'text-rose-600',
};

const SYMBOL: Record<string, typeof CheckCircle2> = {
  gruen: CheckCircle2,
  gelb: AlertTriangle,
  rot: XCircle,
};

export default function BridgeAmpel({
  instanceId, linkId,
}: {
  instanceId: string;
  linkId: string;
}) {
  const [offen, setOffen] = useState(false);

  const pruefung = useMutation({
    mutationFn: () => instancesApi.bridgePruefung(instanceId, linkId),
    onSuccess: (b) => setOffen(!b.steht),
  });

  const b: BridgeBefund | undefined = pruefung.data;

  return (
    <div className="w-full">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => pruefung.mutate()}
          disabled={pruefung.isPending}
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-50"
        >
          <Activity size={12} className={pruefung.isPending ? 'animate-pulse' : ''} />
          {pruefung.isPending ? 'prüft …' : 'Verbindung prüfen'}
        </button>

        {/* Die Ampel: grün heisst, dass eine echte Anfrage durchging — nicht,
            dass die Werte zueinander passen. */}
        {b && (
          <button
            type="button"
            onClick={() => setOffen((v) => !v)}
            className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium ${
              b.steht ? 'bg-emerald-50 text-emerald-800' : 'bg-rose-50 text-rose-800'
            }`}
          >
            {b.steht ? <CheckCircle2 size={12} /> : <XCircle size={12} />}
            {b.steht ? 'Verbindung steht' : (b.bruchstelle ?? 'unterbrochen')}
            <ChevronRight size={11} className={`transition-transform ${offen ? 'rotate-90' : ''}`} />
          </button>
        )}

        {pruefung.isError && (
          <span className="text-xs text-rose-700">
            {(pruefung.error as Error).message}
          </span>
        )}
      </div>

      {b && offen && (
        <div className="mt-2 overflow-hidden rounded-lg border border-slate-200">
          <p
            className={`px-3 py-1.5 text-xs ${
              b.steht ? 'bg-emerald-50 text-emerald-900' : 'bg-rose-50 text-rose-900'
            }`}
          >
            {b.zusammenfassung}
          </p>
          <ul className="divide-y divide-slate-100">
            {b.befunde.map((f, i) => {
              const S = SYMBOL[f.ampel] ?? Circle;
              return (
                <li key={`${f.schritt}-${i}`} className="px-3 py-1.5">
                  <div className="flex items-start gap-1.5">
                    <S size={12} className={`mt-0.5 shrink-0 ${FARBE[f.ampel]}`} />
                    <span className="min-w-0 flex-1">
                      <span className="text-[11px] font-medium text-slate-800">{f.schritt}</span>
                      <span className="ml-1.5 text-[11px] text-slate-500">{f.ergebnis}</span>
                    </span>
                  </div>
                  {(f.deutung || f.rat) && (
                    <div className="mt-0.5 pl-5">
                      {f.deutung && (
                        <p className="text-[10px] leading-relaxed text-slate-600">{f.deutung}</p>
                      )}
                      {f.rat && (
                        <p className="text-[10px] leading-relaxed text-slate-700">
                          <strong className="font-medium">Zu tun:</strong> {f.rat}
                        </p>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
          <p className="border-t border-slate-100 px-3 py-1 text-[10px] text-slate-400">
            {b.wirt} → {b.gast} · {new Date(b.geprueftAm).toLocaleTimeString('de-DE')}
          </p>
        </div>
      )}
    </div>
  );
}
